// =====================================================================================
// INVRT Radar - web console - the ContactCard (story C5, gap G5)
//
// The one card visual for a contact's identity, shared by every surface that names a
// contact: the drawer header, the contacts table row and the visitors feed row render the
// "row" form (the deterministic mark and the name); the full card adds the badges and the
// fields the wire carries, including C3's engagement visits badge when the read has it.
//
// RULED fallbacks, deterministic only: the person's monogram is the initials of the name
// (same name in, same mark out) and the company's lettermark is the first letter of the
// domain's first label, else of the company name. NO image fetching, NO logo resolver and
// NO network calls of any kind - the component reads only the props it is handed (G6
// stays owner-gated). Every mark is decorative and aria-hidden; the name itself stays in
// text where assistive tech reads it.
//
// The `card` prop is the C2 card object the wire carries on GET /contacts/:id
// (_shared/contacts/types.ts): structurally the Contact minus credits and pages, so it is
// typed here as exactly that. A plain Contact works wherever no card object is at hand,
// and `identity` serves the thin feed rows (the visitors feed) whose wire shape is its
// own: the surface resolves the display name with its own fallbacks and hands it over.
// =====================================================================================

import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { contactDisplayName, contactPersonName } from "@/lib/contactDisplay";
import type { Contact } from "@/lib/types.c1";

/** The C2 card object from the wire: the Contact minus credits and pages. */
export type ContactCardObject = Omit<Contact, "credits" | "pages">;

/**
 * The thin identity a feed row carries when the surface holds no full Contact: the
 * display name as the surface resolved it (its own honest fallbacks included), plus the
 * person and company facts the row did carry, for the marks. Nothing is invented here.
 */
export interface ContactCardIdentity {
  /** The display name in text, already resolved by the calling surface. */
  name: string;
  /** The person's name for the initials monogram, when the row carries a person. */
  personName?: string | null;
  /** The company for the lettermark, when the row carries one. */
  company?: { name?: string | null; domain?: string | null } | null;
}

export interface ContactCardProps {
  /** A plain Contact from the wire (a list row or the drawer's read). */
  contact?: Contact;
  /** The C2 card object from GET /contacts/:id; the identity source when both are given. */
  card?: ContactCardObject;
  /** The thin identity for a surface with no Contact at all (the visitors feed). */
  identity?: ContactCardIdentity;
  /** "card" (default): mark, name, badges and fields. "row": mark and name only, for a cell. */
  variant?: "card" | "row";
}

// O8: the person-name and display-name fallbacks are shared (lib/contactDisplay); the
// card, the contacts table, the dossier page and the top-leads panel all read the one
// definition, so no surface can fall back differently from another.

/** The initials monogram: first letters of the first two words, uppercased. Deterministic. */
function initialsOf(name: string): string {
  const words = name.split(/\s+/).filter((word) => word !== "");
  const first = words[0] ?? "";
  if (words.length < 2) return first.slice(0, 2).toUpperCase();
  const second = words[1] ?? "";
  return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
}

/** The company lettermark: first letter of the domain's first label, else of the name. Deterministic. */
function lettermarkOf(company: ContactCardIdentity["company"] | Contact["company"]): string {
  const domain = (company?.domain ?? "")?.trim() ?? "";
  const source = domain !== "" ? domain : ((company?.name ?? "")?.trim() ?? "");
  const label = source.replace(/^www\./i, "").split(/[.\s/]/)[0] ?? "";
  return label.charAt(0).toUpperCase();
}

/** "Manchester, GB" - whichever parts the record carries. */
function locationTextOf(location: {
  city: string | null;
  region: string | null;
  country: string | null;
}): string | null {
  const parts = [location.city, location.region, location.country]
    .map((part) => part?.trim() ?? "")
    .filter((part) => part !== "");
  return parts.length === 0 ? null : parts.join(", ");
}

function CardField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className="text-sm text-ink">{children}</dd>
    </div>
  );
}

const EMPTY_DASH = "-";

/**
 * The contact card. Every mark it renders is derived deterministically from the name or
 * the domain - never fetched - and is aria-hidden; the display name is plain text.
 */
export function ContactCard({ contact, card, identity, variant = "card" }: ContactCardProps) {
  let name: string;
  let personMark = "";
  let companyMark = "";
  if (identity) {
    name = identity.name;
    personMark = identity.personName ? initialsOf(identity.personName) : "";
    companyMark = lettermarkOf(identity.company);
  } else if (card ?? contact) {
    const source = (card ?? contact) as Contact | ContactCardObject;
    name = contactDisplayName(source);
    const person = contactPersonName(source);
    personMark = person === null ? "" : initialsOf(person);
    companyMark = lettermarkOf(source.company);
  } else {
    return null;
  }
  const mark = personMark !== "" ? personMark : companyMark;

  if (variant === "row") {
    return (
      <span className="inline-flex min-w-0 items-center gap-2">
        {mark !== "" ? (
          <span
            aria-hidden="true"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary"
          >
            {mark}
          </span>
        ) : null}
        <span className="truncate">{name}</span>
      </span>
    );
  }

  // The full card: from the C2 card object when the surface holds one, else the Contact.
  const source = (card ?? contact) as Contact | ContactCardObject | undefined;
  const engagement = source?.engagement;
  const company = source?.company;
  const companyLocation = company ? locationTextOf(company.location) : null;

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex min-w-0 items-center gap-3">
        {mark !== "" ? (
          <span
            aria-hidden="true"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-sm font-semibold text-primary"
          >
            {mark}
          </span>
        ) : null}
        <span className="min-w-0 truncate text-base font-semibold text-ink">{name}</span>
      </div>

      {source ? (
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={source.source === "identified" ? "info" : "success"}>
            {source.source === "identified" ? "Identified" : "Revealed"}
          </Badge>
          {source.identified_level ? (
            <Badge tone="neutral">
              {source.identified_level === "person" ? "Person level" : "Company level"}
            </Badge>
          ) : null}
          {source.is_known_customer ? <Badge tone="neutral">Already a customer</Badge> : null}
          {/* C3: the engagement facts the server counted from this organisation's own
              visit records. Facts only: the count as held, no score. */}
          {engagement ? (
            <Badge tone="info">
              {engagement.visit_count} {engagement.visit_count === 1 ? "visit" : "visits"}
            </Badge>
          ) : null}
        </div>
      ) : null}

      {source ? (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
          <CardField label="Title">{source.person.title ?? EMPTY_DASH}</CardField>
          <CardField label="Email">{source.person.email ?? EMPTY_DASH}</CardField>
          <CardField label="Phone">{source.person.phone ?? EMPTY_DASH}</CardField>
          <CardField label="Company">
            {companyMark !== "" && company?.name ? (
              <span className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary-50 text-xs font-semibold text-primary"
                >
                  {companyMark}
                </span>
                {company.name}
              </span>
            ) : (
              (company?.name ?? EMPTY_DASH)
            )}
          </CardField>
          <CardField label="Domain">{company?.domain ?? EMPTY_DASH}</CardField>
          <CardField label="Location">{companyLocation ?? EMPTY_DASH}</CardField>
        </dl>
      ) : null}
    </div>
  );
}
